import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import Chip from '../../../components/Chip.jsx';
import DataTable from '../../../components/DataTable.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { DEFAULT_OPTIMIZE_PARAMETER } from '../../../utils/backtestStudio.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

/**
 * Optimize mode: wraps a base BacktestRequest (from the shared config) with
 * a param grid + objective, per OBT_INTEGRATION_SPEC.md §1 "Optimize/MC/
 * Portfolio/Walkforward wrap a `base: BacktestRequest`." Renders a
 * params→objective leaderboard, best row highlighted.
 */
export default function OptimizeMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun, rulesStrategy }) {
  const [paramName, setParamName] = useState(DEFAULT_OPTIMIZE_PARAMETER);
  const [gridMin, setGridMin] = useState(10);
  const [gridMax, setGridMax] = useState(50);
  const [gridStep, setGridStep] = useState(10);
  const [objective, setObjective] = useState('sharpe');
  const [maximize, setMaximize] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result, true);
  const [error, setError] = useState(null);

  const gridValues = [];
  if ([gridMin, gridMax, gridStep].every(Number.isFinite) && gridStep > 0) {
    for (let i = 0; i < 200 && gridMin + i * gridStep <= gridMax; i++) gridValues.push(gridMin + i * gridStep);
  }

  const request = {
    base: {
      mode: 'rules',
      strategy: rulesStrategy,
      symbols: config.symbols,
      period: config.period,
      config: engineConfig(config),
    },
    grid: { [paramName]: gridValues },
    objective,
    maximize,
  };

  async function run() {
    setError(null);
    setRunning(true);
    try {
      const data = await backtestApi.optimize(request);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  const leaderboard = (result?.leaderboard || []).map((row) => ({
    ...row,
    ...(row.metrics || {}),
    objective: row.objective ?? row.metrics?.[objective],
  }));
  const columns = [
    { key: 'params', header: 'Params', render: (r) => JSON.stringify(r.params) },
    { key: 'cagr', header: 'CAGR', numeric: true, render: (r) => `${r.cagr}%` },
    { key: 'sharpe', header: 'Sharpe', numeric: true },
    { key: 'maxDrawdown', header: 'Max DD', numeric: true, render: (r) => `${r.maxDrawdown}%` },
    { key: 'objective', header: `Objective (${objective})`, numeric: true },
  ];

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Parameter sweep
          </h3>
          <div className="aq-bt-params">
            <label className="aq-field">
              <span>Parameter</span>
              <select value={paramName} onChange={(e) => setParamName(e.target.value)}>
                <option value="config.default_max_hold_bars">Maximum holding bars</option>
                <option value="config.default_sl_pct">Stop-loss fraction</option>
                <option value="config.default_pt_pct">Profit-target fraction</option>
                <option value="config.slippage_mult">Slippage multiplier</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Min</span>
              <input type="number" value={gridMin} onChange={(e) => setGridMin(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Max</span>
              <input type="number" value={gridMax} onChange={(e) => setGridMax(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Step</span>
              <input type="number" value={gridStep} onChange={(e) => setGridStep(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Objective</span>
              <select value={objective} onChange={(e) => setObjective(e.target.value)}>
                <option value="sharpe">Sharpe</option>
                <option value="cagr">CAGR</option>
                <option value="maxDrawdown">Max drawdown</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Direction</span>
              <select value={maximize ? 'max' : 'min'} onChange={(e) => setMaximize(e.target.value === 'max')}>
                <option value="max">Maximize</option>
                <option value="min">Minimize</option>
              </select>
            </label>
          </div>

          <StrategyPreview title="optimize request preview" value={request} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || gridValues.length === 0 || !rulesStrategy || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Optimizing…' : `Run optimize (${gridValues.length} combos)`}
            </Button>
            {!rulesStrategy && <p className="aq-note">Build a strategy in Rules Builder before optimizing it.</p>}
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
          Leaderboard
        </h3>
        <ErrorNote error={error} />
        {!result && !error && <p className="aq-module-desc">Run an optimize sweep to see a leaderboard.</p>}
        {result && (
          <>
            <DataTable
              columns={columns}
              rows={leaderboard.map((r, i) => ({ ...r, id: i }))}
              emptyLabel="No results."
            />
            <div className="aq-metric-row" style={{ marginTop: 10 }}>
              <span>
                  Best: <strong>{JSON.stringify(leaderboard[0]?.params)}</strong>
              </span>
                <Chip tone="gain">Objective {leaderboard[0]?.objective ?? '—'}</Chip>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
